import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Divider,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Link as LinkIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

const LinkingModal = ({ 
  open, 
  onClose, 
  source,  // Ordine o Delivery sorgente
  targets, // Array di potenziali target (Deliveries o Orders)
  type,    // 'order-to-delivery' | 'delivery-to-order'
  onLink,
  loading = false 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [showAll, setShowAll] = useState(false);

  // Reset stato quando cambia la sorgente
  React.useEffect(() => {
    setShowAll(false);
    setSelectedTarget(null);
    setConfirmDialog(null);
  }, [source]);

  // Estrai delivery_code dalla sorgente
  const getDeliveryCode = (item, isOrder) => {
    if (isOrder) {
      // Per ordine: prendi da plant.delivery_code
      return item.plant?.delivery_code || item.contract?.cod_con;
    } else {
      // Per delivery: prendi plant_code (da XML) o plant.delivery_code
      return item.plant_code || item.plant?.delivery_code || item.contract?.cod_con;
    }
  };

  // Calcola score di matching per ogni target
  const scoredTargets = useMemo(() => {
    if (!source || !targets || targets.length === 0) return [];

    const isOrderSource = type === 'order-to-delivery';
    const sourceDeliveryCode = getDeliveryCode(source, isOrderSource);

    return targets.map(target => {
      let score = 0;
      const reasons = [];

      // Estrai dati sorgente
      const sourceQty = parseFloat(source.quantity_liters) || 0;
      const sourceDate = new Date(source.order_date || source.delivery_date);
      const sourceSupplierId = source.supplier_id || source.supplier?.id;

      // Estrai dati target
      const targetDeliveryCode = getDeliveryCode(target, !isOrderSource);
      const targetQty = parseFloat(target.quantity_liters) || 0;
      const targetDate = new Date(target.delivery_date || target.order_date);
      const targetSupplierId = target.supplier_id || target.supplier?.id;

      // Criterio 1: Stesso delivery_code (40 punti)
      if (sourceDeliveryCode && targetDeliveryCode && sourceDeliveryCode === targetDeliveryCode) {
        score += 40;
        reasons.push('✓ Stesso codice consegna');
      } else {
        // Fallback: stesso plant_id
        const sourcePlantId = source.plant_id || source.plant?.id;
        const targetPlantId = target.plant_id || target.plant?.id;
        if (sourcePlantId && targetPlantId && sourcePlantId === targetPlantId) {
          score += 40;
          reasons.push('✓ Stesso impianto');
        } else {
          reasons.push('✗ Codice consegna diverso');
        }
      }

      // Criterio 2: Quantità simile (40 punti max)
      if (sourceQty > 0 && targetQty > 0) {
        const diffPercent = Math.abs(sourceQty - targetQty) / sourceQty * 100;
        if (diffPercent < 5) {
          score += 40;
          reasons.push('✓ Quantità quasi identica');
        } else if (diffPercent < 10) {
          score += 30;
          reasons.push(`≈ Quantità simile (${diffPercent.toFixed(1)}%)`);
        } else if (diffPercent < 20) {
          score += 15;
          reasons.push(`⚠ Differenza quantità ${diffPercent.toFixed(1)}%`);
        } else {
          reasons.push(`⚠ Differenza quantità ${diffPercent.toFixed(1)}%`);
        }
      }

      // Criterio 3: Date vicine (20 punti max)
      const daysDiff = Math.abs((targetDate - sourceDate) / (1000 * 60 * 60 * 24));
      if (daysDiff <= 3) {
        score += 20;
        reasons.push(`✓ Date vicine (${Math.floor(daysDiff)} giorni)`);
      } else if (daysDiff <= 7) {
        score += 15;
        reasons.push(`✓ Entro 7 giorni`);
      } else if (daysDiff <= 14) {
        score += 10;
        reasons.push(`≈ Date distanti ${Math.floor(daysDiff)} giorni`);
      } else {
        reasons.push(`⚠ Date distanti ${Math.floor(daysDiff)} giorni`);
      }

      // Criterio 4: Stesso fornitore (10 punti bonus)
      if (sourceSupplierId && targetSupplierId && sourceSupplierId === targetSupplierId) {
        score += 10;
        reasons.push('✓ Stesso fornitore');
      }

      // Flag per validazione
      const sameDeliveryCode = sourceDeliveryCode && targetDeliveryCode && sourceDeliveryCode === targetDeliveryCode;
      const samePlant = (source.plant_id || source.plant?.id) === (target.plant_id || target.plant?.id);
      const quantityDiff = sourceQty > 0 ? Math.abs(sourceQty - targetQty) / sourceQty * 100 : 0;

      return {
        target,
        score,
        matchLevel: score >= 70 ? 'excellent' : score >= 50 ? 'good' : score >= 30 ? 'fair' : 'weak',
        reasons,
        validation: {
          sameDeliveryCode: sameDeliveryCode || samePlant,
          quantityDiff,
          sourceDeliveryCode,
          targetDeliveryCode
        }
      };
    }).sort((a, b) => b.score - a.score);
  }, [source, targets, type]);

  // Separa target con stesso codice consegna da quelli senza match
  const { matchedTargets, otherTargets } = useMemo(() => {
    const matched = scoredTargets.filter(item => item.validation.sameDeliveryCode);
    const others = scoredTargets.filter(item => !item.validation.sameDeliveryCode);
    return { matchedTargets: matched, otherTargets: others };
  }, [scoredTargets]);

  // Se ci sono match per codice consegna, mostra solo quelli (a meno che showAll)
  // Se non ci sono match, mostra tutti
  const displayedTargets = useMemo(() => {
    if (matchedTargets.length === 0) {
      return scoredTargets;
    }
    if (showAll) {
      return [...matchedTargets, ...otherTargets];
    }
    return matchedTargets;
  }, [matchedTargets, otherTargets, scoredTargets, showAll]);

  const handleSelectTarget = (scoredItem) => {
    const { target, validation } = scoredItem;
    
    // Validazione 1: Stesso codice consegna o impianto (warning)
    if (!validation.sameDeliveryCode) {
      setConfirmDialog({
        type: 'warning',
        title: 'Codice Consegna Diverso',
        message: `L'ordine e il rifornimento hanno codici consegna diversi:
  - Ordine: ${validation.sourceDeliveryCode || 'N/D'}
  - Rifornimento: ${validation.targetDeliveryCode || 'N/D'}

Sei sicuro di voler collegare elementi di impianti diversi?`,
        canProceed: true,
        target
      });
      return;
    }
    
    // Validazione 2: Differenza quantità > 10% (warning)
    if (validation.quantityDiff > 10) {
      setConfirmDialog({
        type: 'warning',
        title: 'Differenza Quantità',
        message: `La differenza di quantità è del ${validation.quantityDiff.toFixed(1)}%.
        
Sei sicuro di voler collegare comunque?`,
        canProceed: true,
        target
      });
      return;
    }

    // Se tutto ok, seleziona direttamente
    setSelectedTarget(target);
  };

  const handleConfirmLink = () => {
    if (confirmDialog?.canProceed && confirmDialog?.target) {
      setSelectedTarget(confirmDialog.target);
      setConfirmDialog(null);
    }
  };

  const handleLink = () => {
    if (!selectedTarget) return;

    if (type === 'order-to-delivery') {
      onLink(source.id, selectedTarget.id);
    } else {
      onLink(selectedTarget.id, source.id);
    }
  };

  const getMatchChipColor = (level) => {
    switch (level) {
      case 'excellent': return 'success';
      case 'good': return 'primary';
      case 'fair': return 'warning';
      default: return 'default';
    }
  };

  const getMatchLabel = (level) => {
    switch (level) {
      case 'excellent': return 'Ottimo';
      case 'good': return 'Buono';
      case 'fair': return 'Discreto';
      default: return 'Debole';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/D';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  const formatQuantity = (qty) => {
    return parseFloat(qty || 0).toLocaleString('it-IT') + ' L';
  };

  const isOrderSource = type === 'order-to-delivery';
  const sourceLabel = isOrderSource ? 'Ordine' : 'Rifornimento';
  const targetLabel = isOrderSource ? 'Rifornimento' : 'Ordine';
  const sourceDeliveryCode = source ? getDeliveryCode(source, isOrderSource) : null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinkIcon />
            Collega {sourceLabel} a {targetLabel}
          </Box>
        </DialogTitle>
        <DialogContent>
          {source && (
            <>
              {/* Sorgente */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'primary.50' }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  {sourceLabel} selezionato
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="text.secondary">Codice</Typography>
                    <Typography fontWeight="bold">
                      {source.order_code || source.delivery_code || source.id}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="body2" color="text.secondary">Data</Typography>
                    <Typography>
                      {formatDate(source.order_date || source.delivery_date)}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="body2" color="text.secondary">Quantità</Typography>
                    <Typography fontWeight="bold" color="primary">
                      {formatQuantity(source.quantity_liters)}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="body2" color="text.secondary">Cod. Consegna</Typography>
                    <Typography fontWeight="bold">
                      {sourceDeliveryCode || 'N/D'}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="text.secondary">Indirizzo</Typography>
                    <Typography variant="body2">
                      {source.plant_address || source.plant?.indirizzo || source.contract?.localita || 'N/D'}
                      {(source.plant?.citta || source.contract?.citta) && `, ${source.plant?.citta || source.contract?.citta}`}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Divider sx={{ my: 2 }} />

              {/* Lista target */}
              <Typography variant="subtitle1" gutterBottom>
                Seleziona {targetLabel} da collegare
              </Typography>

              {scoredTargets.length === 0 ? (
                <Alert severity="info">
                  Nessun {targetLabel.toLowerCase()} disponibile per il collegamento.
                </Alert>
              ) : (
                <>
                  {matchedTargets.length === 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Nessun {targetLabel.toLowerCase()} trovato con lo stesso codice consegna. Vengono mostrati tutti i {targetLabel.toLowerCase()}i disponibili.
                    </Alert>
                  )}
                  {matchedTargets.length > 0 && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {matchedTargets.length} {targetLabel.toLowerCase()}{matchedTargets.length > 1 ? 'i' : 'o'} con stesso codice consegna
                    </Alert>
                  )}
                  <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {displayedTargets.map(({ target, score, matchLevel, reasons, validation }, index) => (
                      <React.Fragment key={target.id}>
                        {showAll && matchedTargets.length > 0 && index === matchedTargets.length && (
                          <Box sx={{ my: 2 }}>
                            <Divider>
                              <Chip label="Altri impianti" size="small" color="default" />
                            </Divider>
                          </Box>
                        )}
                        <ListItem 
                          disablePadding
                          sx={{ mb: 1 }}
                        >
                      <ListItemButton
                        selected={selectedTarget?.id === target.id}
                        onClick={() => handleSelectTarget({ target, score, matchLevel, reasons, validation })}
                        sx={{ 
                          borderRadius: 1,
                          border: '2px solid',
                          borderColor: selectedTarget?.id === target.id 
                            ? 'primary.main' 
                            : matchLevel === 'excellent' 
                              ? 'success.light' 
                              : matchLevel === 'good'
                                ? 'primary.light'
                                : 'divider',
                          bgcolor: !validation.sameDeliveryCode ? 'error.50' : 'transparent'
                        }}
                      >
                        <ListItemText
                          disableTypography
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box>
                                <Typography component="span" fontWeight="bold" display="block">
                                  {target.delivery_code || target.order_code || target.id}
                                </Typography>
                                <Typography component="span" variant="caption" color="text.secondary" display="block">
                                  Cod. Consegna: {getDeliveryCode(target, !isOrderSource) || 'N/D'}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {!validation.sameDeliveryCode && (
                                  <Chip 
                                    label="Impianto diverso" 
                                    size="small"
                                    color="error"
                                    icon={<ErrorIcon />}
                                  />
                                )}
                                <Chip 
                                  label={`${score}%`} 
                                  size="small"
                                  color={getMatchChipColor(matchLevel)}
                                />
                                <Chip 
                                  label={getMatchLabel(matchLevel)} 
                                  size="small"
                                  variant="outlined"
                                  color={getMatchChipColor(matchLevel)}
                                />
                              </Box>
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              {/* Confronto visivo */}
                              <Grid container spacing={1} sx={{ bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>
                                {/* Data */}
                                <Grid item xs={3}>
                                  <Typography component="span" variant="caption" color="text.secondary" display="block">Data</Typography>
                                  {(() => {
                                    const sourceDate = new Date(source.order_date || source.delivery_date);
                                    const targetDate = new Date(target.delivery_date || target.order_date);
                                    const daysDiff = Math.abs((targetDate - sourceDate) / (1000 * 60 * 60 * 24));
                                    const isMatch = daysDiff <= 7;
                                    return (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography 
                                          component="span" 
                                          variant="body2" 
                                          sx={{ 
                                            color: isMatch ? 'success.main' : 'warning.main',
                                            fontWeight: 'bold'
                                          }}
                                        >
                                          {formatDate(target.delivery_date || target.order_date)}
                                        </Typography>
                                        {isMatch ? (
                                          <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                                        ) : (
                                          <Typography component="span" variant="caption" color="warning.main">
                                            ({Math.floor(daysDiff)}gg)
                                          </Typography>
                                        )}
                                      </Box>
                                    );
                                  })()}
                                </Grid>
                                {/* Quantità */}
                                <Grid item xs={3}>
                                  <Typography component="span" variant="caption" color="text.secondary" display="block">Quantità</Typography>
                                  {(() => {
                                    const sourceQty = parseFloat(source.quantity_liters) || 0;
                                    const targetQty = parseFloat(target.quantity_liters) || 0;
                                    const diff = targetQty - sourceQty;
                                    const diffPercent = sourceQty > 0 ? (diff / sourceQty * 100) : 0;
                                    const isMatch = Math.abs(diffPercent) < 5;
                                    const isClose = Math.abs(diffPercent) < 15;
                                    return (
                                      <Box>
                                        <Typography 
                                          component="span" 
                                          variant="body2" 
                                          sx={{ 
                                            color: isMatch ? 'success.main' : isClose ? 'warning.main' : 'error.main',
                                            fontWeight: 'bold'
                                          }}
                                        >
                                          {formatQuantity(targetQty)}
                                        </Typography>
                                        {!isMatch && (
                                          <Typography 
                                            component="span" 
                                            variant="caption" 
                                            sx={{ 
                                              display: 'block',
                                              color: isClose ? 'warning.main' : 'error.main'
                                            }}
                                          >
                                            {diff > 0 ? '+' : ''}{diff.toLocaleString('it-IT')} L ({diffPercent > 0 ? '+' : ''}{diffPercent.toFixed(0)}%)
                                          </Typography>
                                        )}
                                        {isMatch && <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main', ml: 0.5 }} />}
                                      </Box>
                                    );
                                  })()}
                                </Grid>
                                {/* Cod. Consegna */}
                                <Grid item xs={2}>
                                  <Typography component="span" variant="caption" color="text.secondary" display="block">Cod. Consegna</Typography>
                                  {(() => {
                                    const targetCode = getDeliveryCode(target, !isOrderSource);
                                    const isMatch = sourceDeliveryCode && targetCode && sourceDeliveryCode === targetCode;
                                    return (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography 
                                          component="span" 
                                          variant="body2" 
                                          sx={{ 
                                            color: isMatch ? 'success.main' : 'error.main',
                                            fontWeight: 'bold'
                                          }}
                                        >
                                          {targetCode || 'N/D'}
                                        </Typography>
                                        {isMatch ? (
                                          <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                                        ) : (
                                          <ErrorIcon sx={{ fontSize: 14, color: 'error.main' }} />
                                        )}
                                      </Box>
                                    );
                                  })()}
                                </Grid>
                                {/* Indirizzo */}
                                <Grid item xs={4}>
                                  <Typography component="span" variant="caption" color="text.secondary" display="block">Indirizzo</Typography>
                                  {(() => {
                                    const targetAddress = target.plant_address || target.plant?.indirizzo || target.contract?.localita || '';
                                    const sourceAddress = source.plant_address || source.plant?.indirizzo || source.contract?.localita || '';
                                    const isMatch = targetAddress && sourceAddress && 
                                      targetAddress.toLowerCase().includes(sourceAddress.toLowerCase().substring(0, 10)) ||
                                      sourceAddress.toLowerCase().includes(targetAddress.toLowerCase().substring(0, 10));
                                    return (
                                      <Typography 
                                        component="span" 
                                        variant="body2" 
                                        sx={{ 
                                          color: isMatch ? 'success.main' : 'text.secondary',
                                          fontSize: '0.75rem'
                                        }}
                                      >
                                        {targetAddress || 'N/D'}
                                        {isMatch && <CheckCircleIcon sx={{ fontSize: 12, color: 'success.main', ml: 0.5 }} />}
                                      </Typography>
                                    );
                                  })()}
                                </Grid>
                              </Grid>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  </React.Fragment>
                  ))}
                  </List>
                  {matchedTargets.length > 0 && otherTargets.length > 0 && !showAll && (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => setShowAll(true)}
                        sx={{ textTransform: 'none' }}
                      >
                        Mostra tutti gli altri {otherTargets.length} {targetLabel.toLowerCase()}i
                      </Button>
                    </Box>
                  )}
                  {showAll && matchedTargets.length > 0 && (
                    <Box sx={{ textAlign: 'center', mt: 1 }}>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => setShowAll(false)}
                        sx={{ textTransform: 'none' }}
                      >
                        Mostra solo corrispondenze codice consegna
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Annulla
          </Button>
          <Button 
            onClick={handleLink} 
            variant="contained" 
            disabled={loading || !selectedTarget}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            Collega
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog di conferma/errore */}
      <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)} maxWidth="sm" fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {confirmDialog?.type === 'error' ? (
            <ErrorIcon color="error" />
          ) : (
            <WarningIcon color="warning" />
          )}
          {confirmDialog?.title}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ whiteSpace: 'pre-line' }}>
            {confirmDialog?.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)}>
            {confirmDialog?.canProceed ? 'Annulla' : 'Chiudi'}
          </Button>
          {confirmDialog?.canProceed && (
            <Button 
              onClick={handleConfirmLink} 
              variant="contained" 
              color="warning"
            >
              Procedi Comunque
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LinkingModal;
